import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { NEIGHBOUR_SAFETY_TIPS } from "@/components/SafetyTipsDialog";
import {
  ShieldCheck, BadgeCheck, Flag, Mail, HeartHandshake, HandHeart,
  MessageSquare, Receipt, Users, Scale, Eye, ArrowRight,
} from "lucide-react";

// Phase 11 — public safety guide. Tone: warm and practical, never alarmist.
// Content is the single source of truth for safety guidance: the in-flow
// SafetyConfirmDialog / SafetyTipsLink reuse the neighbour tips and link here.
const VOLUNTEERING_TIPS = [
  { icon: BadgeCheck, text: "Look for the verified badge — verified organisations have been independently checked by our team before they can carry it." },
  { icon: MessageSquare, text: "Keep arrangements in CivicHearth messages, so there's always a record if anything needs looking into." },
  { icon: Receipt, text: "Volunteering is unpaid by definition. Genuine hosts may cover expenses — they will never ask you for money." },
  { icon: Users, text: "For roles involving children or vulnerable adults, expect the organisation to ask for references or a DBS check. That's a good sign, not a hurdle." },
  { icon: Eye, text: "First time with a new organisation? Arrive at the advertised venue, meet the named contact, and take a friend along if it helps." },
];

const MODERATION_STEPS = [
  {
    title: "You flag it",
    body: "Every profile, opportunity, story and community post has a report button. Tell us briefly what felt wrong — it takes under a minute.",
  },
  {
    title: "A human reviews it",
    body: "Reports go straight to our moderation queue, where a real person looks at the full context — not an algorithm making guesses.",
  },
  {
    title: "We act, fairly",
    body: "Depending on what we find: content comes down, accounts get warned or suspended, and organisations can lose their verified status. Suspended members can always appeal, and appeals are reviewed by a person too.",
  },
];

export default function Safety() {
  return (
    <div className="bg-cream min-h-screen" data-testid="safety-page">
      <Seo
        title="Safety"
        description="How CivicHearth keeps volunteering and neighbour-to-neighbour help safe — practical tips, verified organisations, and human moderation."
      />

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-terracotta font-bold">
            <ShieldCheck className="w-4 h-4" /> Safety at CivicHearth
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest mt-4 leading-tight tracking-tight">
            Good neighbours look out for each other
          </h1>
          <p className="text-neutral-600 mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Almost every hour given through CivicHearth is exactly what it looks like — kind people
            helping out. A few good habits keep it that way. Here's how we think about safety,
            and what we ask of you.
          </p>
        </Reveal>
      </section>

      {/* Neighbour-to-neighbour */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <Reveal>
          <div className="bg-sky-50 border border-sky-200 rounded-3xl p-8 sm:p-10">
            <div className="text-xs uppercase tracking-[0.18em] text-sky-800 font-bold inline-flex items-center gap-1.5">
              <HeartHandshake className="w-4 h-4" /> Meeting neighbours
            </div>
            <h2 className="font-display text-2xl text-forest mt-3 tracking-tight">When you offer or accept help</h2>
            <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
              Neighbour help means meeting people you don't know yet — that's the whole point, and it's
              lovely. These are the same tips we show before every offer is made or accepted:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3.5 mt-6" data-testid="safety-neighbour-tips">
              {NEIGHBOUR_SAFETY_TIPS.map((t, i) => (
                <li key={t.text} className="flex items-start gap-2.5 text-sm text-neutral-700">
                  <t.icon className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
                  <span>{t.text}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-neutral-500 mt-6">
              Your exact address stays private — requests only show your area, and you share details
              with your chosen helper when you're ready.
            </p>
          </div>
        </Reveal>
      </section>

      {/* General volunteering */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <Reveal>
          <div className="bg-white border border-forest/10 rounded-3xl p-8 sm:p-10">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
              <HandHeart className="w-4 h-4" /> Volunteering with organisations
            </div>
            <h2 className="font-display text-2xl text-forest mt-3 tracking-tight">Signing up for roles and events</h2>
            <ul className="space-y-3.5 mt-6" data-testid="safety-volunteering-tips">
              {VOLUNTEERING_TIPS.map((t, i) => (
                <li key={t.text} className="flex items-start gap-2.5 text-sm text-neutral-700">
                  <t.icon className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                  <span>{t.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* Reporting & moderation */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
            <Scale className="w-4 h-4" /> Behind the scenes
          </div>
          <h2 className="font-display text-2xl text-forest mt-3 tracking-tight">How reporting and moderation work</h2>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            If something ever feels off, you're never on your own with it. Reporting is quick,
            confidential, and every report is read.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {MODERATION_STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div data-testid={`safety-moderation-step-${i + 1}`} className="bg-white border border-forest/10 rounded-2xl p-6 h-full">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-forest/5 text-forest font-display flex items-center justify-center shrink-0">{i + 1}</div>
                  <Flag className="w-4 h-4 text-terracotta" />
                </div>
                <div className="font-display text-lg text-forest mt-4">{s.title}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-4xl mx-auto px-6 py-14 text-center">
        <Reveal>
          <div className="bg-forest text-white rounded-3xl p-10">
            <h2 className="font-display text-3xl leading-tight tracking-tight">Worried about something right now?</h2>
            <p className="text-white/80 mt-3 max-w-xl mx-auto">
              Use the report button on the page in question, or write to us directly — a real person
              reads every message. If anyone is in immediate danger, always call 999 first.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-6">
              <a href="mailto:hello@civichearth.com" data-testid="safety-contact-btn">
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">
                  <Mail className="w-4 h-4 mr-2" /> hello@civichearth.com
                </Button>
              </a>
              <Link to="/about" data-testid="safety-about-btn">
                <Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12">
                  More about CivicHearth <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
